import {
  TREATMENT_AUTHORITY_RESPONSE_KEY,
} from "./treatmentApiConstants";

const ageFromDate = (value) => {
  const birthDate = new Date(value);
  if (Number.isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  if (
    today.getMonth() < birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate())
  ) age -= 1;
  return age;
};

const choiceComparableValue = (choice) => {
  if (!choice || typeof choice !== "object" || Array.isArray(choice)) return choice;
  return choice.value ?? choice.code ?? choice.option_id ?? choice.id;
};

const answerWithChoiceLabels = (question, value) => {
  const actual = value && typeof value === "object" && !Array.isArray(value)
    ? (value.value ?? value.code ?? value.id ?? value)
    : value;
  const choices = question.answer_choices || question.choices || question.options || [];
  if (!Array.isArray(choices) || Array.isArray(actual)) return actual;
  const matched = choices.find((choice) => (
    String(choiceComparableValue(choice) ?? "").trim().toLowerCase()
      === String(actual ?? "").trim().toLowerCase()
  ));
  if (!matched || typeof matched !== "object" || Array.isArray(matched)) return actual;
  const labels = [matched.label, matched.text, matched.name, matched.title]
    .filter((label) => label !== undefined && label !== null && String(label).trim());
  return labels.length ? Array.from(new Set([actual, ...labels])) : actual;
};

export const buildMatchingContext = (
  questionnaire,
  responses,
  targetQuestion = null,
) => {
  const context = {};
  const answerMap = responses || {};
  // The authority may project only the current boundary (for example, the Lab
  // step) and omit already-completed screening questions.  Their responses
  // are still the source of truth for Lab rules, so never discard answer-map
  // entries merely because their question is absent from this projection.
  Object.entries(answerMap).forEach(([key, value]) => {
    context[String(key)] = value;
  });
  const profileVisibility = (
    questionnaire?.runtime_visibility_context?.patient_profile
    || questionnaire?.visibility_context?.patient_profile
    || {}
  );
  context[TREATMENT_AUTHORITY_RESPONSE_KEY.recommendedProgramIds] = (
    Array.isArray(
      answerMap[TREATMENT_AUTHORITY_RESPONSE_KEY.recommendedProgramIds],
    )
      ? answerMap[TREATMENT_AUTHORITY_RESPONSE_KEY.recommendedProgramIds]
      : []
  );
  const targetProgramId = targetQuestion?.program_id
    ? String(targetQuestion.program_id)
    : null;
  (questionnaire?.questions || []).forEach((question) => {
    const responseKey = [
      question.id,
      question.source_id,
      question.source_question_id,
      question.release_node_id,
    ].find((key) => (
      key !== undefined
      && key !== null
      && Object.prototype.hasOwnProperty.call(answerMap, key)
    ));
    if (responseKey === undefined) return;
    const value = answerWithChoiceLabels(question, answerMap[responseKey]);
    context[String(question.id)] = value;
    if (question.release_node_id) {
      context[String(question.release_node_id)] = value;
    }
    const questionProgramId = question.program_id
      ? String(question.program_id)
      : null;
    const canExposeSourceAlias = (
      !questionProgramId
      || (targetProgramId && questionProgramId === targetProgramId)
    );
    if (question.source_id) {
      if (canExposeSourceAlias || context[String(question.source_id)] === undefined) {
        context[String(question.source_id)] = value;
      }
    }
    if (question.source_question_id) {
      if (canExposeSourceAlias || context[String(question.source_question_id)] === undefined) {
        context[String(question.source_question_id)] = value;
      }
    }

    const mappings = [
      question.beluga_field_mapping,
      question.mapped_field,
      question.mappedField,
    ].filter(Boolean);
    if (canExposeSourceAlias) {
      mappings.forEach((mapping) => {
        context[String(mapping)] = value;
      });
    }
    if (question.question_type === "state" || question.question_type === "state_routing") {
      context.service_state = typeof value === "object" ? value.value || value.code : value;
    }
    if (question.question_type === "personal_details" && value && typeof value === "object") {
      Object.assign(context, value);
    }
    if (["bmi", "height_weight"].includes(question.question_type) && value && typeof value === "object") {
      const feet = Number(value.feet);
      const inches = Number(value.inches);
      const weight = Number(value.weight);
      const totalInches = feet * 12 + inches;
      if (totalInches > 0 && weight > 0) {
        context.bmi = Math.round(((weight / (totalInches * totalInches)) * 703) * 10) / 10;
        context.__derived_bmi__ = context.bmi;
      }
    }
  });

  // Released Program questions use `program:<program-id>:<source-id>` as an
  // UI identity.  Lab rules are authored against the stable source ID.  Add
  // that source alias even when the earlier question is no longer present in
  // the authority's current-boundary questionnaire projection.
  if (targetProgramId) {
    const prefix = `program:${targetProgramId}:`;
    Object.entries(answerMap).forEach(([key, value]) => {
      if (String(key).startsWith(prefix)) {
        context[String(key).slice(prefix.length)] = value;
      }
    });
  }

  if (context.date_of_birth && context.age == null) {
    context.age = ageFromDate(context.date_of_birth);
  }
  if (context.dob && context.age == null) context.age = ageFromDate(context.dob);
  if (context.gender && !context.sex) context.sex = context.gender;
  if (context.sex && !context.gender) context.gender = context.sex;
  if (typeof context.sex === "string") {
    context.sex = context.sex.trim().toLowerCase();
  }
  if (typeof context.gender === "string") {
    context.gender = context.gender.trim().toLowerCase();
  }
  const profileSex = typeof profileVisibility.sex === "string"
    ? profileVisibility.sex.trim().toLowerCase()
    : profileVisibility.sex;
  const profileAge = numericProfileValue(profileVisibility.age);
  if (profileSex !== undefined && profileSex !== null && profileSex !== "") {
    context.sex = profileSex;
    context.gender = profileSex;
  }
  if (profileAge !== null) context.age = profileAge;
  context.patient_profile = {
    sex: profileSex ?? null,
    age: profileAge,
  };
  context["patient_profile.sex"] = profileSex ?? null;
  context["patient_profile.age"] = profileAge;
  if (context.state && !context.service_state) {
    context.service_state = typeof context.state === "object"
      ? context.state.value || context.state.code
      : context.state;
  }
  return context;
};

const numericProfileValue = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};
